import {
  Card,
  getToken,
  ColorsV2,
  Address,
  Flex,
  Radio,
  Icon,
  Text,
  spaces,
  Chip,
  useThemeColors,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { formatAddressForDisplay } from './helpers';

const AddressOptionContainer = styled(Card)<{ $isSelected: boolean }>`
  width: 100%;
  border-color: ${getToken(ColorsV2.BORDER)};
  box-shadow: none;

  &:hover {
    padding: 15px;
    border-width: ${spaces.space_3xs};
    border-color: ${getToken(ColorsV2.TEXT_LIGHT)};
    background-color: transparent;
  }

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      padding: 15px;
      border-width: ${spaces.space_3xs};
      border-color: ${getToken(ColorsV2.TEXT_LIGHT)};
      background-color: ${getToken(ColorsV2.PRIMARY_LIGHTEST)};

      &:hover {
        background-color: ${getToken(ColorsV2.PRIMARY_LIGHTEST)};
      }
    `}
`;

type AddressOptionCardProps = {
  address: Address;
  id: string;
  label: string;
  isSelected: boolean;
  onClick: () => void;
  isRecommended?: boolean;
};

const AddressOptionCard = ({
  address,
  id,
  label,
  isSelected,
  onClick,
  isRecommended,
}: AddressOptionCardProps) => {
  const { t } = useTranslation('common');
  const [
    badgeBackgroundColor,
    badgeTextColor,
    iconColor,
    recommendedIconColor,
  ] = useThemeColors([
    ColorsV2.SUCCESS_LIGHTEST,
    ColorsV2.SUCCESS_DARK,
    ColorsV2.GRAY_DARK,
    ColorsV2.SUCCESS,
  ]);

  return (
    <AddressOptionContainer
      id={id}
      $isSelected={isSelected}
      p={spaces.space_m}
      onClick={onClick}>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" mb={spaces.space_xs}>
          <Radio id={`${id}-radio`} checked={isSelected} value={id} />
          <Icon
            icon="pin"
            color={isRecommended ? recommendedIconColor : iconColor}
          />
          <Text variant="body" ml={spaces.space_xs}>
            {label}
          </Text>
        </Flex>
        {isRecommended && (
          <Chip
            id="recommended-badge"
            variant="small"
            backgroundColor={badgeBackgroundColor}
            textColor={badgeTextColor}>
            {t('addressOptionCard.recommended')}
          </Chip>
        )}
      </Flex>
      <Flex vertical>
        <Text variant="label-large">
          {formatAddressForDisplay(address).street}
        </Text>
        <Text variant="label-large">
          {formatAddressForDisplay(address).location}
        </Text>
      </Flex>
    </AddressOptionContainer>
  );
};

export default AddressOptionCard;
